import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { COLORS, AVATARS, LIFE_SKILLS, REWARD_PRESETS } from '../constants';
import { Card, Button, Input, Modal, Badge, StatCard, AvatarIcon } from './UI';

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginBottom: 10, borderRadius: 12, border: `1px solid ${COLORS.border}`, overflow: "hidden" }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: "100%", padding: "14px 16px", background: open ? COLORS.surfaceGreen : COLORS.white, border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", textAlign: "left" }}>
        <span style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, color: COLORS.text }}>{question}</span>
        <span style={{ fontSize: 18, color: COLORS.primary }}>{open ? "−" : "+"}</span>
      </button>
      {open && <div style={{ padding: "0 16px 14px", fontFamily: "Nunito, sans-serif", fontSize: 13, color: COLORS.textMuted, lineHeight: 1.6 }}>{answer}</div>}
    </div>
  );
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const ms = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(ms / 60000);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d === 1) return 'yesterday';
  return `${d} days ago`;
}

const XP_LEVELS = [0, 100, 250, 500, 800, 1200, 1700, 2300, 3000, 4000];
function getChildLevel(xp) {
  let lvl = 1;
  for (let i = 1; i < XP_LEVELS.length; i++) {
    if (xp >= XP_LEVELS[i]) lvl = i + 1; else break;
  }
  return lvl;
}

export default function ParentApp({ parent, onLogout, children, tasks, rewards, onDataChange }) {
  const [screen, setScreen] = useState("dashboard");
  const [screenData, setScreenData] = useState(null);
  const [showAddChild, setShowAddChild] = useState(false);
  const [showAssignTask, setShowAssignTask] = useState(false);
  const [showAddReward, setShowAddReward] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [libraryCategory, setLibraryCategory] = useState(Object.keys(LIFE_SKILLS)[0]);
  const [addedSkillNames, setAddedSkillNames] = useState(new Set());
  const [libraryToast, setLibraryToast] = useState(false);
  const [suggestionPoints, setSuggestionPoints] = useState({});
  const [childForm, setChildForm] = useState({ name: "", age: "", avatar: AVATARS[0], pin: "" });
  const [taskForm, setTaskForm] = useState({ name: "", points: "10", emoji: "⭐", frequency: "Daily", childId: "", due_date: "" });
  const [rewardForm, setRewardForm] = useState({ name: "", points: "50", emoji: "🏆", childId: "" });
  const [saving, setSaving] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [editReward, setEditReward] = useState(null);
  const [editTaskForm, setEditTaskForm] = useState({});
  const [editRewardForm, setEditRewardForm] = useState({});
  const [showJournal, setShowJournal] = useState(false);
  const [journalChild, setJournalChild] = useState(null);
  const [journalEntries, setJournalEntries] = useState([]);
  const [journalComments, setJournalComments] = useState({});
  const [journalHearts, setJournalHearts] = useState({});
  const [insightsChild, setInsightsChild] = useState(null);
  const [insightsMoods, setInsightsMoods] = useState([]);
  const [insightsRange, setInsightsRange] = useState(30);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [editChild, setEditChild] = useState(null);
  const [editChildForm, setEditChildForm] = useState({});
  const [confirmRemoveChild, setConfirmRemoveChild] = useState(false);

  const [recentJournals, setRecentJournals] = useState([]);

  // Reload journals whenever children roster changes (catches add/remove) or on mount
  useEffect(() => {
    if (!parent?.id) return;
    supabase
      .from('journals')
      .select('id, child_id, created_at')
      .eq('parent_id', parent.id)
      .order('created_at', { ascending: false })
      .limit(20)
      .then(({ data }) => setRecentJournals(data || []));
  }, [parent.id, children.length]);

  const pendingTasks = tasks.filter(t => t.status === "pending");
  const pendingRewards = rewards.filter(r => r.status === "pending_redemption");
  const pendingSuggestedTasks   = tasks.filter(t => t.suggested_by_kid && t.suggestion_status === 'pending');
  const pendingSuggestedRewards = rewards.filter(r => r.suggested_by_kid && r.suggestion_status === 'pending');
  const totalPoints = children.reduce((s, c) => s + (c.points || 0), 0);

  // Build activity feed from tasks, rewards, journals already in memory
  const activityFeed = (() => {
    const items = [];

    tasks
      .filter(t => t.status === 'pending' || t.status === 'completed')
      .forEach(t => {
        const child = children.find(c => c.id === t.child_id);
        if (!child) return;
        items.push({
          key: `task-${t.id}`,
          avatar: child.avatar,
          childName: child.name,
          text: `completed ${t.name} ${t.emoji}`,
          time: t.updated_at || t.created_at,
        });
      });

    rewards
      .filter(r => r.status === 'pending_redemption' || r.status === 'redeemed')
      .forEach(r => {
        const child = children.find(c => c.id === r.child_id);
        if (!child) return;
        items.push({
          key: `reward-${r.id}`,
          avatar: child.avatar,
          childName: child.name,
          text: r.status === 'redeemed'
            ? `redeemed ${r.emoji} ${r.name} 🎉`
            : `requested a reward — ${r.emoji} ${r.name} 🏆`,
          time: r.updated_at || r.created_at,
        });
      });

    recentJournals.forEach(j => {
      const child = children.find(c => c.id === j.child_id);
      if (!child) return;
      items.push({
        key: `journal-${j.id}`,
        avatar: child.avatar,
        childName: child.name,
        text: 'wrote a journal entry 📓',
        time: j.created_at,
      });
    });

    return items
      .sort((a, b) => new Date(b.time) - new Date(a.time))
      .slice(0, 10);
  })();

  const addChild = async () => {
    if (!childForm.name || !childForm.age || childForm.pin.length !== 4) return;
    setSaving(true);
    await supabase.from('children').insert({ parent_id: parent.id, name: childForm.name, age: parseInt(childForm.age), avatar: childForm.avatar, pin: childForm.pin, points: 0 });
    setChildForm({ name: "", age: "", avatar: AVATARS[0], pin: "" });
    setShowAddChild(false);
    onDataChange();
    setSaving(false);
  };

  const addTask = async () => {
    if (!taskForm.name || !taskForm.childId) return;
    setSaving(true);
    await supabase.from('tasks').insert({ parent_id: parent.id, child_id: taskForm.childId, name: taskForm.name, emoji: taskForm.emoji, points: parseInt(taskForm.points), frequency: taskForm.frequency, due_date: taskForm.due_date || null, status: 'active' });
    setTaskForm({ name: "", points: "10", emoji: "⭐", frequency: "Daily", childId: "", due_date: "" });
    setShowAssignTask(false);
    setShowLibrary(false);
    onDataChange();
    setSaving(false);
  };

  const addFromLibrary = async (skill, category) => {
    if (!taskForm.childId) { alert("Select a child first"); return; }
    await supabase.from('tasks').insert({ parent_id: parent.id, child_id: taskForm.childId, name: skill.name, emoji: skill.emoji, points: skill.points, frequency: 'Daily', category, status: 'active', due_date: new Date().toISOString().split('T')[0] });
    onDataChange();
    setAddedSkillNames(prev => new Set([...prev, skill.name]));
    setLibraryToast(true);
    setTimeout(() => setLibraryToast(false), 2000);
  };

  const addReward = async () => {
    if (!rewardForm.name || !rewardForm.childId) return;
    setSaving(true);
    await supabase.from('rewards').insert({ parent_id: parent.id, child_id: rewardForm.childId, name: rewardForm.name, emoji: rewardForm.emoji, points: parseInt(rewardForm.points) });
    setRewardForm({ name: "", points: "50", emoji: "🏆", childId: "" });
    setShowAddReward(false);
    onDataChange();
    setSaving(false);
  };

  const approveTask = async (task) => {
    await supabase.from('tasks').update({ status: 'completed' }).eq('id', task.id);
    const child = children.find(c => c.id === task.child_id);
    const today = new Date().toISOString().split('T')[0];
    const lastDate = child?.last_streak_date;
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];
    const newStreak = lastDate === today ? child?.streak || 1 : lastDate === yesterdayStr ? (child?.streak || 0) + 1 : 1;
    await supabase.from('children').update({ points: (child?.points || 0) + task.points, streak: newStreak, last_streak_date: today }).eq('id', task.child_id);
    await supabase.rpc('increment_xp', { child_id: task.child_id, amount: 5 });
    onDataChange();
  };

  const approveReward = async (reward) => {
    const child = children.find(c => c.id === reward.child_id);
    const currentPoints = child?.points || 0;
    if (currentPoints < reward.points) { alert("Not enough coins!"); return; }
    await supabase.from('rewards').update({ status: 'redeemed' }).eq('id', reward.id);
    await supabase.from('children').update({ points: currentPoints - reward.points }).eq('id', reward.child_id);
    onDataChange();
  };

  const denyReward = async (reward) => {
    await supabase.from('rewards').update({ status: null }).eq('id', reward.id);
    onDataChange();
  };

  const approveSuggestedTask = async (task) => {
    const points = parseInt(suggestionPoints[task.id] ?? task.points) || task.points;
    await supabase.from('tasks').update({ status: 'active', suggestion_status: 'approved', points }).eq('id', task.id);
    onDataChange();
  };

  const rejectSuggestedTask = async (task) => {
    await supabase.from('tasks').delete().eq('id', task.id);
    onDataChange();
  };

  const approveSuggestedReward = async (reward) => {
    const points = parseInt(suggestionPoints[reward.id] ?? reward.points) || reward.points;
    await supabase.from('rewards').update({ suggestion_status: 'approved', points }).eq('id', reward.id);
    onDataChange();
  };

  const rejectSuggestedReward = async (reward) => {
    await supabase.from('rewards').delete().eq('id', reward.id);
    onDataChange();
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm("Delete this mission?")) return;
    await supabase.from('tasks').delete().eq('id', taskId);
    onDataChange();
  };

  const deleteReward = async (rewardId) => {
    if (!window.confirm("Delete this reward?")) return;
    await supabase.from('rewards').delete().eq('id', rewardId);
    onDataChange();
  };

  const saveEditTask = async () => {
    setSaving(true);
    await supabase.from('tasks').update({ name: editTaskForm.name, points: parseInt(editTaskForm.points), emoji: editTaskForm.emoji, frequency: editTaskForm.frequency }).eq('id', editTask.id);
    setEditTask(null);
    onDataChange();
    setSaving(false);
  };

  const saveEditReward = async () => {
    setSaving(true);
    await supabase.from('rewards').update({ name: editRewardForm.name, points: parseInt(editRewardForm.points), emoji: editRewardForm.emoji }).eq('id', editReward.id);
    setEditReward(null);
    onDataChange();
    setSaving(false);
  };

  const saveEditChild = async () => {
    if (!editChildForm.name) return;
    if (editChildForm.pin && editChildForm.pin.length > 0 && editChildForm.pin.length !== 4) return;
    setSaving(true);
    const childUpdate = { name: editChildForm.name, avatar: editChildForm.avatar, age: parseInt(editChildForm.age) || editChild.age };
    if (editChildForm.pin && editChildForm.pin.length === 4) childUpdate.pin = editChildForm.pin;
    await supabase.from('children').update(childUpdate).eq('id', editChild.id);
    if (screenData?.id === editChild.id) {
      setScreenData(prev => ({ ...prev, name: editChildForm.name, avatar: editChildForm.avatar, age: parseInt(editChildForm.age) || editChild.age }));
    }
    setEditChild(null);
    onDataChange();
    setSaving(false);
  };

  const removeChild = async () => {
    if (!editChild || editChild.parent_id !== parent.id) return;
    setSaving(true);
    await supabase.from('children').delete().eq('id', editChild.id).eq('parent_id', parent.id);
    if (screenData?.id === editChild.id) {
      setScreen('dashboard');
      setScreenData(null);
    }
    setConfirmRemoveChild(false);
    setEditChild(null);
    onDataChange();
    setSaving(false);
  };

  const loadJournal = async (child) => {
    setJournalChild(child);
    const { data } = await supabase.from('journals').select('*').eq('child_id', child.id).order('created_at', { ascending: false });
    setJournalEntries(data || []);
    // Initialize hearts and comments from saved data
    const hearts = {};
    const comments = {};
    (data || []).forEach(e => {
      if (e.parent_hearted) hearts[e.id] = true;
      if (e.parent_comment) comments[e.id] = e.parent_comment;
    });
    setJournalHearts(hearts);
    setJournalComments(comments);
    setShowJournal(true);
  };

  const loadInsights = async (child, range) => {
    setInsightsLoading(true);
    const since = new Date();
    since.setDate(since.getDate() - range);
    const { data } = await supabase.from('mood_checkins').select('*').eq('child_id', child.id).gte('created_at', since.toISOString()).order('created_at', { ascending: true });
    setInsightsMoods(data || []);
    setInsightsLoading(false);
  };

  const switchInsightsChild = async (child) => {
    setInsightsChild(child);
    await loadInsights(child, insightsRange);
  };

  const switchInsightsRange = async (range) => {
    setInsightsRange(range);
    if (insightsChild) await loadInsights(insightsChild, range);
  };

  return (
    <div style={{ minHeight: "100vh", background: COLORS.bg, maxWidth: 480, margin: "0 auto" }}>
      <div style={{ paddingBottom: 80 }}>

        {screen === "dashboard" && (
          <div>
            <div style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`, padding: "28px 20px", borderRadius: "0 0 24px 24px", marginBottom: 24 }}>
              <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: COLORS.accentLight, fontWeight: 600, marginBottom: 4 }}>GOOD {new Date().getHours() < 12 ? "MORNING" : new Date().getHours() < 17 ? "AFTERNOON" : "EVENING"}</div>
              <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 28, color: "#fff" }}>{parent.name} 👋</div>
              <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: COLORS.accentLight, marginTop: 4 }}>{children.length} children · {tasks.length} missions</div>
              <button onClick={() => setShowCode(true)} style={{ marginTop: 12, background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 10, color: "#fff", fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 12, padding: "6px 14px", cursor: "pointer" }}>🔑 Family Code: {parent.family_code}</button>
            </div>

            <div style={{ padding: "0 16px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 24 }}>
                <StatCard label="Total Coins" value={totalPoints} emoji="⭐" />
                <StatCard label="Pending" value={pendingTasks.length + pendingRewards.length} emoji="🔔" color={COLORS.warning} />
                <StatCard label="Children" value={children.length} emoji="👨‍👧" />
              </div>

              {/* ── Needs Attention ── */}
              {(() => {
                const kidsWithNoProgress = children.filter(child => {
                  const real = tasks.filter(t => t.child_id === child.id && !t.suggested_by_kid &&
                    (t.status === 'active' || t.status === 'pending' || t.status === 'completed'));
                  return real.length > 0 && real.every(t => t.status === 'active');
                });

                const attnItems = [
                  ...pendingTasks.map(t => {
                    const ch = children.find(c => c.id === t.child_id);
                    return {
                      key: `pt-${t.id}`, icon: t.emoji, type: 'task',
                      text: `${ch?.name || 'Someone'} completed ${t.name}`,
                      onApprove: () => approveTask(t),
                    };
                  }),
                  ...pendingRewards.map(r => {
                    const ch = children.find(c => c.id === r.child_id);
                    return {
                      key: `pr-${r.id}`, icon: r.emoji, type: 'reward',
                      text: `${ch?.name || 'Someone'} requested ${r.name}`,
                      onApprove: () => approveReward(r),
                    };
                  }),
                  ...(pendingSuggestedTasks.length + pendingSuggestedRewards.length > 0 ? [{
                    key: 'suggestions', icon: '💡', type: 'suggestion',
                    text: `${pendingSuggestedTasks.length + pendingSuggestedRewards.length} kid suggestion${pendingSuggestedTasks.length + pendingSuggestedRewards.length !== 1 ? 's' : ''} waiting for review`,
                    label: 'Review ↓',
                  }] : []),
                  ...kidsWithNoProgress.map(ch => ({
                    key: `idle-${ch.id}`, icon: ch.avatar, type: 'idle',
                    text: `${ch.name} hasn't started any missions today 👀`,
                    onNav: () => { setScreenData(ch); setScreen('childDetail'); },
                  })),
                ];

                if (attnItems.length === 0) return null;

                return (
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 20, color: COLORS.text, marginBottom: 12 }}>Needs Attention 🚨</div>
                    <div style={{ background: "#FFFBEB", border: `1px solid #FDE68A`, borderLeft: `4px solid #F59E0B`, borderRadius: 12, overflow: "hidden" }}>
                      {attnItems.map((item, i) => (
                        <div
                          key={item.key}
                          onClick={item.onNav}
                          style={{
                            display: "flex", alignItems: "center", gap: 12,
                            padding: "12px 14px",
                            borderBottom: i < attnItems.length - 1 ? `1px solid #FDE68A` : "none",
                            cursor: item.onNav ? "pointer" : "default",
                          }}
                        >
                          <div style={{ fontSize: item.type === 'idle' ? 26 : 20, flexShrink: 0, lineHeight: 1 }}>{item.icon}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 13, color: COLORS.text, lineHeight: 1.35 }}>
                              {item.text}
                            </div>
                          </div>
                          {item.onApprove && (
                            <button
                              onClick={e => { e.stopPropagation(); item.onApprove(); }}
                              style={{ background: COLORS.success, border: "none", borderRadius: 8, color: "#fff", fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 12, padding: "7px 13px", cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}
                            >
                              ✅ Approve
                            </button>
                          )}
                          {item.label && !item.onApprove && (
                            <span style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: "#92400E", fontWeight: 700, flexShrink: 0 }}>{item.label}</span>
                          )}
                          {item.onNav && !item.onApprove && !item.label && (
                            <span style={{ fontSize: 18, color: COLORS.textLight }}>›</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {pendingTasks.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 20, color: COLORS.text, marginBottom: 12 }}>Missions Awaiting Approval 🔔</div>
                  {pendingTasks.map(task => {
                    const child = children.find(c => c.id === task.child_id);
                    return (
                      <Card key={task.id} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                        <div style={{ fontSize: 24 }}>{task.emoji}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, color: COLORS.text }}>{task.name}</div>
                          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: COLORS.textMuted }}>{child?.avatar} {child?.name}</div>
                        </div>
                        <Badge>+{task.points} coins</Badge>
                        <Button onClick={() => approveTask(task)} size="sm" variant="success">Approve ✓</Button>
                      </Card>
                    );
                  })}
                </div>
              )}

              {pendingRewards.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 20, color: COLORS.text, marginBottom: 12 }}>Reward Requests 🎁</div>
                  {pendingRewards.map(reward => {
                    const child = children.find(c => c.id === reward.child_id);
                    return (
                      <Card key={reward.id} style={{ marginBottom: 10, background: "#FFF3E0", border: `1px solid ${COLORS.warning}` }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                          <div style={{ fontSize: 28 }}>{reward.emoji}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, color: COLORS.text }}>{reward.name}</div>
                            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: COLORS.textMuted }}>{child?.avatar} {child?.name} · {reward.points} coins</div>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <Button onClick={() => approveReward(reward)} size="sm" variant="success" style={{ flex: 1 }}>✓ Approve & Deduct Coins</Button>
                          <Button onClick={() => denyReward(reward)} size="sm" variant="danger" style={{ flex: 1 }}>✗ Deny</Button>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}

              {(pendingSuggestedTasks.length > 0 || pendingSuggestedRewards.length > 0) && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 20, color: COLORS.text, marginBottom: 12 }}>Kid Suggestions 💡</div>

                  {pendingSuggestedTasks.length > 0 && (
                    <>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Missions</div>
                      {pendingSuggestedTasks.map(task => {
                        const taskChild = children.find(c => c.id === task.child_id);
                        const pts = suggestionPoints[task.id] ?? String(task.points);
                        return (
                          <Card key={task.id} style={{ marginBottom: 10, background: "#FFFBEB", border: `1px solid #FDE68A` }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                              <div style={{ fontSize: 26 }}>{task.emoji}</div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, color: COLORS.text }}>{task.name}</div>
                                <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: COLORS.textMuted }}>{taskChild?.avatar} {taskChild?.name} suggests this mission</div>
                              </div>
                              <span style={{ fontSize: 18 }}>💡</span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                              <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, fontWeight: 700, color: COLORS.textMuted }}>Coins:</div>
                              <input type="number" min={1} value={pts} onChange={e => setSuggestionPoints(p => ({ ...p, [task.id]: e.target.value }))} style={{ width: 72, padding: "6px 10px", borderRadius: 8, border: `1.5px solid ${COLORS.border}`, fontFamily: "Nunito, sans-serif", fontSize: 13, outline: "none", background: COLORS.surfaceGreen }} />
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                              <Button onClick={() => approveSuggestedTask(task)} size="sm" variant="success" style={{ flex: 1 }}>✓ Approve</Button>
                              <Button onClick={() => rejectSuggestedTask(task)} size="sm" variant="danger" style={{ flex: 1 }}>✗ Reject</Button>
                            </div>
                          </Card>
                        );
                      })}
                    </>
                  )}

                  {pendingSuggestedRewards.length > 0 && (
                    <>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, marginTop: pendingSuggestedTasks.length > 0 ? 12 : 0 }}>Rewards</div>
                      {pendingSuggestedRewards.map(reward => {
                        const rewardChild = children.find(c => c.id === reward.child_id);
                        const pts = suggestionPoints[reward.id] ?? String(reward.points);
                        return (
                          <Card key={reward.id} style={{ marginBottom: 10, background: "#FFFBEB", border: `1px solid #FDE68A` }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                              <div style={{ fontSize: 26 }}>{reward.emoji}</div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, color: COLORS.text }}>{reward.name}</div>
                                <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: COLORS.textMuted }}>{rewardChild?.avatar} {rewardChild?.name} suggests this reward</div>
                              </div>
                              <span style={{ fontSize: 18 }}>💡</span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                              <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, fontWeight: 700, color: COLORS.textMuted }}>Coins:</div>
                              <input type="number" min={1} value={pts} onChange={e => setSuggestionPoints(p => ({ ...p, [reward.id]: e.target.value }))} style={{ width: 72, padding: "6px 10px", borderRadius: 8, border: `1.5px solid ${COLORS.border}`, fontFamily: "Nunito, sans-serif", fontSize: 13, outline: "none", background: COLORS.surfaceGreen }} />
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                              <Button onClick={() => approveSuggestedReward(reward)} size="sm" variant="success" style={{ flex: 1 }}>✓ Approve</Button>
                              <Button onClick={() => rejectSuggestedReward(reward)} size="sm" variant="danger" style={{ flex: 1 }}>✗ Reject</Button>
                            </div>
                          </Card>
                        );
                      })}
                    </>
                  )}
                </div>
              )}

              <div style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 20, color: COLORS.text }}>Children</div>
                  <Button onClick={() => setShowAddChild(true)} size="sm">+ Add</Button>
                </div>
                {children.length === 0 ? (
                  <Card style={{ textAlign: "center", padding: 32, background: COLORS.surfaceGreen }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>👨‍👧‍👦</div>
                    <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 15, color: COLORS.textMuted, fontWeight: 600 }}>No children added yet</div>
                    <Button onClick={() => setShowAddChild(true)} style={{ marginTop: 16 }}>Add Your First Child</Button>
                  </Card>
                ) : children.map(child => {
                  // --- per-card derived data ---
                  const childTasks = tasks.filter(t => t.child_id === child.id && t.status !== 'pending_approval');
                  const doneTasks  = childTasks.filter(t => t.status === 'pending' || t.status === 'completed');
                  const total = childTasks.length;
                  const done  = doneTasks.length;
                  const pct   = total > 0 ? Math.round((done / total) * 100) : 0;
                  const allDone = total > 0 && done === total;

                  const streak = child.streak || 0;
                  const level  = getChildLevel(child.xp || 0);

                  // last active: newest updated_at on a pending/completed task, else mood date
                  const lastTaskTs = childTasks
                    .filter(t => (t.status === 'pending' || t.status === 'completed') && t.updated_at)
                    .map(t => t.updated_at)
                    .sort()
                    .at(-1);
                  let lastActiveStr = '';
                  if (lastTaskTs) {
                    lastActiveStr = `active ${timeAgo(lastTaskTs)}`;
                  } else if (child.last_mood_date) {
                    const d = new Date(child.last_mood_date);
                    if (!isNaN(d)) lastActiveStr = `active ${timeAgo(d.toISOString())}`;
                  }

                  return (
                    <Card key={child.id} onClick={() => { setScreenData(child); setScreen("childDetail"); }} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                      {/* Avatar */}
                      <div style={{ fontSize: 36, flexShrink: 0, lineHeight: 1 }}>{child.avatar}</div>

                      {/* Middle column */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Row 1: name + streak + level */}
                        <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "nowrap", overflow: "hidden" }}>
                          <span style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 15, color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{child.name}</span>
                          {streak > 0 && (
                            <span style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 12, color: "#F97316", flexShrink: 0 }}>🔥{streak}</span>
                          )}
                          <span style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 10, background: "#FEF3C7", color: "#B45309", borderRadius: 5, padding: "2px 6px", flexShrink: 0 }}>Lvl {level}</span>
                        </div>

                        {/* Row 2: progress bar */}
                        {total > 0 && (
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 5 }}>
                            <div style={{ flex: 1, background: COLORS.border, borderRadius: 3, height: 5, overflow: "hidden" }}>
                              <div style={{ height: "100%", width: `${pct}%`, background: allDone ? COLORS.success : COLORS.primaryLighter, borderRadius: 3, transition: "width 0.4s" }} />
                            </div>
                            <span style={{ fontFamily: "Nunito, sans-serif", fontSize: 10, fontWeight: 700, color: allDone ? COLORS.success : COLORS.textMuted, whiteSpace: "nowrap", flexShrink: 0 }}>
                              {allDone ? "✅ All done!" : `${done} / ${total}`}
                            </span>
                          </div>
                        )}

                        {/* Row 3: last active */}
                        {lastActiveStr !== '' && (
                          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 10, color: COLORS.textLight, marginTop: 3 }}>{lastActiveStr}</div>
                        )}
                      </div>

                      {/* Right: coins */}
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 20, color: COLORS.primary }}>{child.points || 0}</div>
                        <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 10, color: COLORS.textMuted }}>coins</div>
                      </div>

                      {/* Edit */}
                      <button onClick={e => { e.stopPropagation(); setEditChild(child); setEditChildForm({ name: child.name, avatar: child.avatar, age: String(child.age || ''), pin: '' }); }} style={{ background: COLORS.accentLighter, border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14, padding: "6px 10px", color: COLORS.primary, flexShrink: 0 }}>✏️</button>
                    </Card>
                  );
                })}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {[["📋","Assign Mission",() => setShowAssignTask(true)],["🏆","Add Reward",() => setShowAddReward(true)],["📊","All Missions",() => setScreen("tasks")],["🔑","Family Code",() => setShowCode(true)]].map(([icon,label,action]) => (
                  <Card key={label} onClick={action} style={{ textAlign: "center", padding: "20px 16px", background: COLORS.surfaceGreen }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>{icon}</div>
                    <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, fontWeight: 700, color: COLORS.primary }}>{label}</div>
                  </Card>
                ))}
              </div>

              {/* ── This Week ── */}
              {(() => {
                const now = new Date();
                const daysFromMon = now.getDay() === 0 ? 6 : now.getDay() - 1;
                const weekStart = new Date(now);
                weekStart.setDate(weekStart.getDate() - daysFromMon);
                weekStart.setHours(0, 0, 0, 0);

                const weekTasks = tasks.filter(t =>
                  (t.status === 'pending' || t.status === 'completed') &&
                  t.updated_at && new Date(t.updated_at) >= weekStart
                );
                const weekMissions = weekTasks.length;
                const weekCoins = weekTasks.reduce((s, t) => s + (t.points || 0), 0);
                const bestStreakKid = children.length > 0
                  ? children.reduce((best, c) => (c.streak || 0) > (best.streak || 0) ? c : best)
                  : null;
                const bestStreak = bestStreakKid?.streak || 0;

                return (
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 20, color: COLORS.text, marginBottom: 12 }}>This Week 📊</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                      <div style={{ background: COLORS.surfaceGreen, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "14px 10px", textAlign: "center" }}>
                        <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 28, color: COLORS.primary, lineHeight: 1 }}>{weekMissions}</div>
                        <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 10, fontWeight: 700, color: COLORS.textMuted, marginTop: 4, lineHeight: 1.3 }}>Missions Done</div>
                      </div>
                      <div style={{ background: "#FFFBEB", border: `1px solid #FDE68A`, borderRadius: 14, padding: "14px 10px", textAlign: "center" }}>
                        <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 28, color: "#D97706", lineHeight: 1 }}>{weekCoins}</div>
                        <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 10, fontWeight: 700, color: COLORS.textMuted, marginTop: 4, lineHeight: 1.3 }}>Coins Earned</div>
                      </div>
                      <div style={{ background: "#FFF7ED", border: `1px solid #FED7AA`, borderRadius: 14, padding: "14px 10px", textAlign: "center" }}>
                        {bestStreak > 0 ? (
                          <>
                            <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 24, color: "#EA580C", lineHeight: 1 }}>{bestStreak}🔥</div>
                            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 10, fontWeight: 700, color: COLORS.textMuted, marginTop: 4, lineHeight: 1.3 }}>{bestStreakKid?.name?.split(' ')[0]}'s streak</div>
                          </>
                        ) : (
                          <>
                            <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 28, color: COLORS.textLight, lineHeight: 1 }}>—</div>
                            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 10, fontWeight: 700, color: COLORS.textMuted, marginTop: 4, lineHeight: 1.3 }}>Best Streak</div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Recent Activity Feed */}
              <div style={{ marginTop: 4, marginBottom: 24 }}>
                <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 20, color: COLORS.text, marginBottom: 12 }}>Recent Activity 📋</div>
                <div style={{ background: "#F4F6F5", border: `1px solid ${COLORS.border}`, borderRadius: 16, overflow: "hidden" }}>
                  {activityFeed.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "28px 16px" }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: COLORS.textMuted }}>No recent activity yet — it'll show up here as your kids get going!</div>
                    </div>
                  ) : activityFeed.map((item, i) => (
                    <div key={item.key} style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "11px 14px",
                      background: i % 2 === 0 ? "#fff" : "#F9FBFA",
                      borderBottom: i < activityFeed.length - 1 ? `1px solid ${COLORS.border}` : "none",
                    }}>
                      {/* Kid avatar */}
                      <div style={{ fontSize: 26, flexShrink: 0, lineHeight: 1 }}>{item.avatar}</div>

                      {/* Activity text */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 13, color: COLORS.text }}>
                          {item.childName}{" "}
                        </span>
                        <span style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: COLORS.textMuted }}>
                          {item.text}
                        </span>
                      </div>

                      {/* Time ago */}
                      <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, color: COLORS.textLight, flexShrink: 0, whiteSpace: "nowrap" }}>
                        {timeAgo(item.time)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {screen === "childDetail" && screenData && (
          <div>
            <div style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`, padding: "20px 16px 28px", borderRadius: "0 0 24px 24px", marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <button onClick={() => setScreen("dashboard")} style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 10, color: "#fff", padding: "6px 14px", fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>← Back</button>
                <button onClick={() => { setEditChild(screenData); setEditChildForm({ name: screenData.name, avatar: screenData.avatar, age: String(screenData.age || ''), pin: '' }); }} style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 10, color: "#fff", padding: "6px 14px", fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>✏️ Edit Profile</button>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ fontSize: 52 }}>{screenData.avatar}</div>
                <div>
                  <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 26, color: "#fff" }}>
                    {screenData.name}{screenData.username && <span style={{ fontFamily: "Nunito, sans-serif", fontSize: 16, fontWeight: 700, color: COLORS.accentLight, marginLeft: 8 }}>(@{screenData.username})</span>}
                  </div>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: COLORS.accentLight }}>Age {screenData.age}</div>
                </div>
                <div style={{ marginLeft: "auto", textAlign: "right" }}>
                  <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 32, color: "#FFD93D" }}>{screenData.points || 0}</div>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: COLORS.accentLight }}>coins</div>
                </div>
              </div>
            </div>
            <div style={{ padding: "0 16px" }}>
              <Button onClick={() => { setTaskForm(f => ({ ...f, childId: screenData.id })); setShowAssignTask(true); }} style={{ width: "100%", marginBottom: 12 }}>+ Assign Mission</Button>
              <Button onClick={() => { setRewardForm(f => ({ ...f, childId: screenData.id })); setShowAddReward(true); }} variant="secondary" style={{ width: "100%", marginBottom: 20 }}>+ Add Reward</Button>
              {tasks.filter(t => t.child_id === screenData.id).map(task => (
                <Card key={task.id} style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ fontSize: 24 }}>{task.emoji}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, color: COLORS.text }}>{task.name}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 2 }}>
                        <span style={{ fontFamily: "Nunito, sans-serif", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: task.status === "completed" ? "#E8F5E9" : task.status === "pending" ? "#FFF3E0" : COLORS.accentLighter, color: task.status === "completed" ? COLORS.success : task.status === "pending" ? COLORS.warning : COLORS.primary }}>
                          {task.status === "completed" ? "✅ Done" : task.status === "pending" ? "⏳ Needs Approval" : "🎯 Active"}
                        </span>
                        {task.due_date && <span style={{ fontFamily: "Nunito, sans-serif", fontSize: 10, fontWeight: 700, color: new Date(task.due_date) < new Date() ? "#DC2626" : COLORS.textMuted }}>📅 Due {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                      </div>
                    </div>
                    <Badge>+{task.points}</Badge>
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                    {task.status === "pending" && <Button onClick={() => approveTask(task)} size="sm" variant="success">✓ Approve</Button>}
                    <button onClick={() => { setEditTask(task); setEditTaskForm({ name: task.name, points: String(task.points), emoji: task.emoji, frequency: task.frequency || "Daily" }); }} style={{ background: COLORS.accentLighter, border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, padding: "4px 10px", fontFamily: "Nunito, sans-serif", fontWeight: 700, color: COLORS.primary }}>✏️ Edit</button>
                    <button onClick={() => deleteTask(task.id)} style={{ background: "#FEE2E2", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, padding: "4px 10px", fontFamily: "Nunito, sans-serif", fontWeight: 700, color: "#DC2626" }}>🗑️ Delete</button>
                  </div>
                </Card>
              ))}
              {rewards.filter(r => r.child_id === screenData.id).length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 18, color: COLORS.text, marginBottom: 12 }}>Rewards 🏆</div>
                  {rewards.filter(r => r.child_id === screenData.id).map(reward => (
                    <Card key={reward.id} style={{ marginBottom: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ fontSize: 24 }}>{reward.emoji}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, color: COLORS.text }}>{reward.name}</div>
                          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: COLORS.textMuted }}>{reward.points} coins required</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                        <button onClick={() => { setEditReward(reward); setEditRewardForm({ name: reward.name, points: String(reward.points), emoji: reward.emoji }); }} style={{ background: COLORS.accentLighter, border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, padding: "4px 10px", fontFamily: "Nunito, sans-serif", fontWeight: 700, color: COLORS.primary }}>✏️ Edit</button>
                        <button onClick={() => deleteReward(reward.id)} style={{ background: "#FEE2E2", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, padding: "4px 10px", fontFamily: "Nunito, sans-serif", fontWeight: 700, color: "#DC2626" }}>🗑️ Delete</button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {screen === "tasks" && (
          <div style={{ padding: "20px 16px 0" }}>
            <button onClick={() => setScreen("dashboard")} style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 16, background: COLORS.accentLighter, border: "none", borderRadius: 10, color: COLORS.primary, padding: "8px 14px", fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>← Back</button>
            <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 26, color: COLORS.text, marginBottom: 20 }}>All Missions 📋</div>
            {tasks.filter(t => t.status !== "completed").length === 0 ? (
              <Card style={{ textAlign: "center", padding: 32, background: COLORS.surfaceGreen }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🎉</div>
                <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 14, color: COLORS.textMuted }}>All missions completed and approved!</div>
              </Card>
            ) : tasks.filter(t => t.status !== "completed").map(task => {
              const child = children.find(c => c.id === task.child_id);
              return (
                <Card key={task.id} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ fontSize: 24 }}>{task.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, color: COLORS.text }}>{task.name}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 2 }}>
                        <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: COLORS.textMuted }}>{child?.avatar} {child?.name}</div>
                        {task.due_date && <span style={{ fontFamily: "Nunito, sans-serif", fontSize: 10, fontWeight: 700, color: new Date(task.due_date) < new Date() ? "#DC2626" : COLORS.textMuted }}>📅 Due {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                      </div>
                    </div>
                    <Badge>+{task.points}</Badge>
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                    {task.status === "pending" && <Button onClick={() => approveTask(task)} size="sm" variant="success">✓ Approve</Button>}
                    <button onClick={() => { setEditTask(task); setEditTaskForm({ name: task.name, points: String(task.points), emoji: task.emoji, frequency: task.frequency || "Daily" }); }} style={{ background: COLORS.accentLighter, border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, padding: "4px 10px", fontFamily: "Nunito, sans-serif", fontWeight: 700, color: COLORS.primary }}>✏️ Edit</button>
                    <button onClick={() => deleteTask(task.id)} style={{ background: "#FEE2E2", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, padding: "4px 10px", fontFamily: "Nunito, sans-serif", fontWeight: 700, color: "#DC2626" }}>🗑️ Delete</button>
                  </div>
                </Card>
              );
            })}
            <Button onClick={() => setShowAssignTask(true)} style={{ width: "100%", marginTop: 8 }}>+ Assign Mission</Button>
          </div>
        )}

        {screen === "rewards" && (
          <div style={{ padding: "20px 16px 0" }}>
            <button onClick={() => setScreen("dashboard")} style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 16, background: COLORS.accentLighter, border: "none", borderRadius: 10, color: COLORS.primary, padding: "8px 14px", fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>← Back</button>
            <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 26, color: COLORS.text, marginBottom: 20 }}>Rewards 🏆</div>
            {children.map(child => {
              const childRewards = rewards.filter(r => r.child_id === child.id);
              return (
                <div key={child.id} style={{ marginBottom: 24 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 16, color: COLORS.text }}>{child.avatar} {child.name}</div>
                    <Button size="sm" onClick={() => { setRewardForm(f => ({ ...f, childId: child.id })); setShowAddReward(true); }}>+ Add</Button>
                  </div>
                  {childRewards.length === 0 ? (
                    <Card style={{ textAlign: "center", padding: 20, background: COLORS.surfaceGreen }}>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: COLORS.textMuted }}>No rewards yet — add one!</div>
                    </Card>
                  ) : childRewards.map(reward => (
                    <Card key={reward.id} style={{ marginBottom: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ fontSize: 24 }}>{reward.emoji}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, color: COLORS.text }}>{reward.name}</div>
                          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: COLORS.textMuted }}>{reward.points} coins required</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                        <button onClick={() => { setEditReward(reward); setEditRewardForm({ name: reward.name, points: String(reward.points), emoji: reward.emoji }); }} style={{ background: COLORS.accentLighter, border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, padding: "4px 10px", fontFamily: "Nunito, sans-serif", fontWeight: 700, color: COLORS.primary }}>✏️ Edit</button>
                        <button onClick={() => deleteReward(reward.id)} style={{ background: "#FEE2E2", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, padding: "4px 10px", fontFamily: "Nunito, sans-serif", fontWeight: 700, color: "#DC2626" }}>🗑️ Delete</button>
                      </div>
                    </Card>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {screen === "insights" && (
          <div style={{ padding: "20px 16px 0" }}>
            <button onClick={() => setScreen("dashboard")} style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 16, background: COLORS.accentLighter, border: "none", borderRadius: 10, color: COLORS.primary, padding: "8px 14px", fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>← Back</button>
            <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 26, color: COLORS.text, marginBottom: 4 }}>Insights 📊</div>
            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: COLORS.textMuted, marginBottom: 20 }}>Track moods, streaks & growth</div>

            {children.length === 0 ? (
              <Card style={{ textAlign: "center", padding: 32 }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>👨‍👧‍👦</div>
                <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 14, color: COLORS.textMuted }}>Add children to see insights</div>
              </Card>
            ) : (
              <>
                {/* Child selector */}
                <div style={{ display: "flex", gap: 10, marginBottom: 20, overflowX: "auto", paddingBottom: 4 }}>
                  {children.map(child => (
                    <button key={child.id} onClick={() => switchInsightsChild(child)} style={{ flexShrink: 0, padding: "8px 16px", borderRadius: 20, border: `2px solid ${insightsChild?.id === child.id ? COLORS.primary : COLORS.border}`, background: insightsChild?.id === child.id ? COLORS.primary : COLORS.white, color: insightsChild?.id === child.id ? "#fff" : COLORS.text, fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                      {child.avatar} {child.name}
                    </button>
                  ))}
                </div>

                {insightsChild && (
                  <>
                    {/* Streak Card */}
                    {(() => {
                      const childTasks = tasks.filter(t => t.child_id === insightsChild.id);
                      const completed = childTasks.filter(t => t.status === "completed").length;
                      const total = childTasks.length;
                      const streak = insightsChild.streak || 0;
                      const points = insightsChild.points || 0;
                      return (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
                          <Card style={{ textAlign: "center", padding: 16, background: "linear-gradient(135deg, #FFF3E0, #fff)" }}>
                            <div style={{ fontSize: 28 }}>🔥</div>
                            <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 28, color: "#F97316" }}>{streak}</div>
                            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, color: COLORS.textMuted, fontWeight: 700 }}>Day Streak</div>
                          </Card>
                          <Card style={{ textAlign: "center", padding: 16, background: "linear-gradient(135deg, #F0FDF4, #fff)" }}>
                            <div style={{ fontSize: 28 }}>✅</div>
                            <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 28, color: COLORS.success }}>{completed}</div>
                            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, color: COLORS.textMuted, fontWeight: 700 }}>Missions Done</div>
                          </Card>
                          <Card style={{ textAlign: "center", padding: 16, background: "linear-gradient(135deg, #FFFBEB, #fff)" }}>
                            <div style={{ fontSize: 28 }}>⭐</div>
                            <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 28, color: "#EAB308" }}>{points}</div>
                            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, color: COLORS.textMuted, fontWeight: 700 }}>Coins</div>
                          </Card>
                        </div>
                      );
                    })()}

                    {/* Range selector */}
                    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                      {[30, 60, 90].map(r => (
                        <button key={r} onClick={() => switchInsightsRange(r)} style={{ flex: 1, padding: "8px 0", borderRadius: 10, border: `2px solid ${insightsRange === r ? COLORS.primary : COLORS.border}`, background: insightsRange === r ? COLORS.primary : COLORS.white, color: insightsRange === r ? "#fff" : COLORS.text, fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                          {r} days
                        </button>
                      ))}
                    </div>

                    {/* Mood Pattern */}
                    <Card style={{ marginBottom: 20 }}>
                      <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 18, color: COLORS.text, marginBottom: 4 }}>😊 Mood Patterns</div>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: COLORS.textMuted, marginBottom: 16 }}>Last {insightsRange} days</div>
                      {insightsLoading ? (
                        <div style={{ textAlign: "center", padding: 20, color: COLORS.textMuted }}>Loading...</div>
                      ) : insightsMoods.length === 0 ? (
                        <div style={{ textAlign: "center", padding: 20 }}>
                          <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
                          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: COLORS.textMuted }}>No mood data yet</div>
                        </div>
                      ) : (
                        <>
                          {/* Day of week breakdown */}
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, fontWeight: 700, color: COLORS.textMuted, marginBottom: 10 }}>BY DAY OF WEEK</div>
                            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((day, i) => {
                              const dayMoods = insightsMoods.filter(m => new Date(m.created_at).getDay() === i);
                              if (dayMoods.length === 0) return null;
                              const moodCounts = dayMoods.reduce((acc, m) => { acc[m.mood] = (acc[m.mood] || 0) + 1; return acc; }, {});
                              const topMood = Object.entries(moodCounts).sort((a,b) => b[1]-a[1])[0];
                              return (
                                <div key={day} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                                  <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, fontWeight: 700, color: COLORS.textMuted, width: 32 }}>{day}</div>
                                  <div style={{ flex: 1, background: COLORS.border, borderRadius: 10, height: 8, overflow: "hidden" }}>
                                    <div style={{ height: "100%", width: `${(dayMoods.length / insightsMoods.length) * 100}%`, background: COLORS.primary, borderRadius: 10 }} />
                                  </div>
                                  <div style={{ fontSize: 18 }}>{topMood[0]}</div>
                                  <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, color: COLORS.textMuted, width: 24 }}>{dayMoods.length}x</div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Mood frequency */}
                          <div>
                            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, fontWeight: 700, color: COLORS.textMuted, marginBottom: 10 }}>MOOD FREQUENCY</div>
                            {Object.entries(insightsMoods.reduce((acc, m) => { acc[m.mood] = (acc[m.mood] || 0) + 1; return acc; }, {})).sort((a,b) => b[1]-a[1]).map(([mood, count]) => (
                              <div key={mood} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                                <div style={{ fontSize: 22 }}>{mood}</div>
                                <div style={{ flex: 1, background: COLORS.border, borderRadius: 10, height: 8, overflow: "hidden" }}>
                                  <div style={{ height: "100%", width: `${(count / insightsMoods.length) * 100}%`, background: COLORS.primaryLighter, borderRadius: 10 }} />
                                </div>
                                <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, fontWeight: 700, color: COLORS.textMuted, width: 30 }}>{Math.round((count/insightsMoods.length)*100)}%</div>
                              </div>
                            ))}
                          </div>

                          {/* Actionable insight */}
                          {(() => {
                            const moodCounts = insightsMoods.reduce((acc, m) => { acc[m.mood] = (acc[m.mood] || 0) + 1; return acc; }, {});
                            const sadMoods = ["😢","😔","😟"].reduce((sum, e) => sum + (moodCounts[e] || 0), 0);
                            const pct = Math.round((sadMoods / insightsMoods.length) * 100);
                            if (pct >= 30) return (
                              <div style={{ marginTop: 16, padding: 14, background: "#FEF3C7", borderRadius: 12, border: "1px solid #FDE68A" }}>
                                <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 13, color: "#92400E", marginBottom: 4 }}>💡 Insight for you</div>
                                <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: "#92400E", lineHeight: 1.5 }}>{insightsChild.name} has checked in with lower moods {pct}% of the time recently. This may be worth discussing with a counselor or teacher.</div>
                              </div>
                            );
                            return null;
                          })()}
                        </>
                      )}
                    </Card>

                    {/* Growth Timeline */}
                    <Card style={{ marginBottom: 80 }}>
                      <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 18, color: COLORS.text, marginBottom: 4 }}>📈 Growth Timeline</div>
                      <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: COLORS.textMuted, marginBottom: 16 }}>Coins earned over {insightsRange} days</div>
                      {insightsMoods.length === 0 ? (
                        <div style={{ textAlign: "center", padding: 20 }}>
                          <div style={{ fontSize: 32, marginBottom: 8 }}>🌱</div>
                          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: COLORS.textMuted }}>Growth data will appear as {insightsChild.name} uses the app</div>
                        </div>
                      ) : (
                        <div style={{ overflowX: "auto" }}>
                          <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 80, minWidth: Math.max(insightsMoods.length * 20, 280), paddingBottom: 4 }}>
                            {insightsMoods.map((entry, i) => {
                              const happyMoods = ["😊","😄","🤩","😁"];
                              const isHappy = happyMoods.includes(entry.mood);
                              const height = isHappy ? 60 + Math.random() * 20 : 20 + Math.random() * 30;
                              return (
                                <div key={i} title={new Date(entry.created_at).toLocaleDateString()} style={{ flex: 1, minWidth: 12, height: `${height}%`, background: isHappy ? COLORS.primary : "#FCA5A5", borderRadius: "4px 4px 0 0", transition: "height 0.3s" }} />
                              );
                            })}
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, color: COLORS.textMuted }}>{insightsRange} days ago</div>
                            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, color: COLORS.textMuted }}>Today</div>
                          </div>
                          <div style={{ display: "flex", gap: 16, marginTop: 12 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 12, height: 12, borderRadius: 3, background: COLORS.primary }} /><span style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, color: COLORS.textMuted }}>Positive mood</span></div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 12, height: 12, borderRadius: 3, background: "#FCA5A5" }} /><span style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, color: COLORS.textMuted }}>Lower mood</span></div>
                          </div>
                        </div>
                      )}
                    </Card>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {screen === "profile" && (
          <div style={{ padding: "20px 16px 0" }}>
            <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 26, color: COLORS.text, marginBottom: 20 }}>Profile 👤</div>
            <Card style={{ textAlign: "center", padding: 28, marginBottom: 16 }}>
              <AvatarIcon name={parent.name} size={64} />
              <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 22, color: COLORS.text, marginTop: 12 }}>{parent.name}</div>
              <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: COLORS.textMuted }}>{parent.email}</div>
              <div style={{ marginTop: 16, padding: 12, background: COLORS.surfaceGreen, borderRadius: 12 }}>
                <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: COLORS.textMuted }}>Current Plan</div>
                <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 18, color: COLORS.primary }}>Free Plan</div>
                <div style={{ marginTop: 8, padding: "8px 16px", borderRadius: 20, background: "#e5e7eb", display: "inline-block" }}>
                  <span style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, fontWeight: 700, color: "#9ca3af" }}>Pro Plan — Coming Soon</span>
                </div>
              </div>
            </Card>
            <Card style={{ marginBottom: 16 }}>
              <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, color: COLORS.text, marginBottom: 8 }}>🔑 Your Family Code</div>
              <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 28, color: COLORS.primary, letterSpacing: 4, textAlign: "center", padding: "16px", background: COLORS.surfaceGreen, borderRadius: 12 }}>{parent.family_code}</div>
              <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: COLORS.textMuted, marginTop: 8, textAlign: "center" }}>Share this with your kids so they can log in</div>
            </Card>
            <Card style={{ marginBottom: 16 }}>
              <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, color: COLORS.text, marginBottom: 12 }}>📓 Children's Journals</div>
              {children.length === 0 ? (
                <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: COLORS.textMuted, textAlign: "center", padding: 16 }}>No children added yet</div>
              ) : children.map(child => (
                <div key={child.id} onClick={() => loadJournal(child)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: `1px solid ${COLORS.border}`, cursor: "pointer" }}>
                  <div style={{ fontSize: 28 }}>{child.avatar}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, color: COLORS.text }}>{child.name}</div>
                    <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: COLORS.textMuted }}>View mood journal →</div>
                  </div>
                </div>
              ))}
            </Card>
            <Button variant="danger" style={{ width: "100%" }} onClick={onLogout}>Sign Out</Button>

            <div style={{ marginTop: 24, marginBottom: 80 }}>
              <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 20, color: COLORS.text, marginBottom: 16 }}>❓ FAQ</div>
              {[
                { q: "How does my child log in?", a: "Share your Family Code with them. They tap 'I'm a Kid', enter the code, pick their name, and enter their 4-digit PIN." },
                { q: "How do coins work?", a: "Each mission has a coin value. When your child marks a mission done and you approve it, the coins are added to their account automatically." },
                { q: "What are rewards?", a: "Rewards are goals you set for your child. Once they earn enough coins, they can redeem them for the reward you set (like extra screen time, a special outing, etc.)." },
                { q: "What is the mood check-in?", a: "Each time a child logs in, they do a quick mood check-in. This helps them build emotional awareness. You can view their entries in Children's Journals." },
                { q: "What is the Life Skills Library?", a: "A curated collection of age-appropriate missions across 5 categories: Self-Care, Emotional Regulation, Helping at Home, Social Skills, and Independence Skills — all grounded in ABA principles." },
                { q: "Can I have more than one child?", a: "Yes! You can add multiple children, each with their own missions, rewards, PIN, and avatar. Pro plan (coming soon) expands the number of children you can add." },
                { q: "Is my family's data private?", a: "Yes. Your data is stored securely and is never shared with third parties. Each family has a unique code and children can only access their own profile." },
                { q: "What is my child's default PIN?", a: "Every child's default PIN is 0000. You can change it anytime from the Edit Child screen, or your child can change it themselves from their profile." },
                { q: "How do I reset a child's PIN?", a: "Tap the ✏️ icon next to any child's name to open the Edit Child screen, then enter a new 4-digit PIN and save. Your child can also change their own PIN from the Me tab in their app." },
              ].map((item, i) => (
                <FAQItem key={i} question={item.q} answer={item.a} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 480, background: COLORS.surface, borderTop: `1px solid ${COLORS.border}`, display: "flex", zIndex: 100, boxShadow: "0 -4px 20px rgba(0,0,0,0.08)" }}>
        {[["🏠","Home","dashboard"],["📋","Missions","tasks"],["🏆","Rewards","rewards"],["📊","Insights","insights"],["👤","Profile","profile"]].map(([icon,label,s]) => (
          <button key={s} onClick={() => { setScreen(s); if (s === "insights" && children.length > 0 && !insightsChild) { setInsightsChild(children[0]); loadInsights(children[0], insightsRange); } }} style={{ flex: 1, padding: "10px 0", border: "none", background: "transparent", cursor: "pointer", color: screen === s ? COLORS.primary : COLORS.textLight, fontFamily: "Nunito, sans-serif", fontSize: 9, fontWeight: 700, display: "flex", flexDirection: "column", alignItems: "center", gap: 2, borderTop: screen === s ? `2px solid ${COLORS.primary}` : "2px solid transparent", transition: "all 0.2s" }}>
            <span style={{ fontSize: 18 }}>{icon}</span>{label}
          </button>
        ))}
      </div>

      {/* Modals */}
      <Modal show={showAddChild} onClose={() => setShowAddChild(false)} title="Add a Child 👶">
        <Input label="Child's Name" value={childForm.name} onChange={v => setChildForm(f => ({ ...f, name: v }))} placeholder="Emma" />
        <Input label="Age" value={childForm.age} onChange={v => setChildForm(f => ({ ...f, age: v }))} placeholder="8" type="number" />
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, fontWeight: 700, color: COLORS.textMuted, marginBottom: 8 }}>Choose Avatar</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {AVATARS.map(av => (
              <button key={av} onClick={() => setChildForm(f => ({ ...f, avatar: av }))} style={{ fontSize: 32, padding: 8, borderRadius: 12, border: `2px solid ${childForm.avatar === av ? COLORS.primary : COLORS.border}`, background: childForm.avatar === av ? COLORS.accentLighter : COLORS.surfaceGreen, cursor: "pointer", transition: "all 0.2s" }}>{av}</button>
            ))}
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, fontWeight: 700, color: COLORS.textMuted, marginBottom: 6 }}>Set 4-Digit PIN</div>
          <input value={childForm.pin} onChange={e => { if (/^\d{0,4}$/.test(e.target.value)) setChildForm(f => ({ ...f, pin: e.target.value })); }} placeholder="e.g. 1234" type="password" maxLength={4}
            style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `2px solid ${COLORS.border}`, fontFamily: "Nunito, sans-serif", color: COLORS.text, background: COLORS.surfaceGreen, outline: "none", boxSizing: "border-box", letterSpacing: 8, textAlign: "center", fontSize: 24 }} />
        </div>
        <Button onClick={addChild} style={{ width: "100%" }} size="lg" disabled={saving || !childForm.name || !childForm.age || childForm.pin.length !== 4}>
          {saving ? "Adding..." : "Add Child 💚"}
        </Button>
      </Modal>

      <Modal show={showAssignTask} onClose={() => { setShowAssignTask(false); setShowLibrary(false); setAddedSkillNames(new Set()); }} title={showLibrary ? "Life Skills Library 📚" : "Assign a Mission 📋"}>
        {!showLibrary ? (
          <>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, fontWeight: 700, color: COLORS.textMuted, marginBottom: 6 }}>Child</div>
              <select value={taskForm.childId} onChange={e => setTaskForm(f => ({ ...f, childId: e.target.value }))} style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `2px solid ${COLORS.border}`, fontFamily: "Nunito, sans-serif", fontSize: 14, background: COLORS.surfaceGreen, color: COLORS.text, outline: "none" }}>
                <option value="">Select a child...</option>
                {children.map(c => <option key={c.id} value={c.id}>{c.avatar} {c.name}</option>)}
              </select>
            </div>
            <Button onClick={() => setShowLibrary(true)} variant="secondary" style={{ width: "100%", marginBottom: 16 }}>📚 Pick from Life Skills Library</Button>
            <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 16, marginBottom: 8 }}>
              <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textAlign: "center", marginBottom: 12 }}>— OR CREATE CUSTOM —</div>
            </div>
            <Input label="Mission Name" value={taskForm.name} onChange={v => setTaskForm(f => ({ ...f, name: v }))} placeholder="e.g. Clean your room" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Input label="Coins" value={taskForm.points} onChange={v => setTaskForm(f => ({ ...f, points: v }))} placeholder="10" type="number" />
              <Input label="Emoji" value={taskForm.emoji} onChange={v => setTaskForm(f => ({ ...f, emoji: v }))} placeholder="⭐" />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, fontWeight: 700, color: COLORS.textMuted, marginBottom: 6 }}>Frequency</div>
              <select value={taskForm.frequency} onChange={e => setTaskForm(f => ({ ...f, frequency: e.target.value }))} style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `2px solid ${COLORS.border}`, fontFamily: "Nunito, sans-serif", fontSize: 14, background: COLORS.surfaceGreen, color: COLORS.text, outline: "none" }}>
                {["Daily","Weekly","One-time"].map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, fontWeight: 700, color: COLORS.textMuted, marginBottom: 6 }}>Due Date <span style={{ fontWeight: 400, fontSize: 11 }}>(optional)</span></div>
              <input type="date" value={taskForm.due_date} onChange={e => setTaskForm(f => ({ ...f, due_date: e.target.value }))} style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `2px solid ${COLORS.border}`, fontFamily: "Nunito, sans-serif", fontSize: 14, background: COLORS.surfaceGreen, color: COLORS.text, outline: "none", boxSizing: "border-box" }} />
            </div>
            <Button onClick={addTask} style={{ width: "100%" }} size="lg" disabled={saving}>Assign Mission ✓</Button>
          </>
        ) : (
          <>
            {libraryToast && (
              <div style={{ background: "#D1FAE5", border: "1px solid #6EE7B7", borderRadius: 10, padding: "10px 16px", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 18 }}>✅</span>
                <span style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 14, color: "#065F46" }}>Mission added!</span>
              </div>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
              {Object.keys(LIFE_SKILLS).map(cat => (
                <button key={cat} onClick={() => setLibraryCategory(cat)} style={{ padding: "6px 14px", borderRadius: 20, border: "none", cursor: "pointer", fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 12, background: libraryCategory === cat ? COLORS.primary : COLORS.accentLighter, color: libraryCategory === cat ? "#fff" : COLORS.primary, transition: "all 0.2s" }}>{cat}</button>
              ))}
            </div>
            {LIFE_SKILLS[libraryCategory].map(skill => (
              <div key={skill.name} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: `1px solid ${COLORS.border}` }}>
                <div style={{ fontSize: 22 }}>{skill.emoji}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, color: COLORS.text }}>{skill.name}</div>
                  <Badge>+{skill.points} coins</Badge>
                </div>
                {addedSkillNames.has(skill.name)
                  ? <button disabled style={{ fontSize: 20, padding: "4px 8px", borderRadius: 8, border: "none", background: "transparent", cursor: "default" }}>✅</button>
                  : <Button onClick={() => addFromLibrary(skill, libraryCategory)} size="sm">+ Add</Button>
                }
              </div>
            ))}
            <Button onClick={() => setShowLibrary(false)} variant="secondary" style={{ width: "100%", marginTop: 16 }}>← Back</Button>
          </>
        )}
      </Modal>

      <Modal show={showAddReward} onClose={() => setShowAddReward(false)} title="Add a Reward 🏆">
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, fontWeight: 700, color: COLORS.textMuted, marginBottom: 8 }}>Quick Add</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {REWARD_PRESETS.map(r => (
              <button key={r.name} onClick={() => setRewardForm(f => ({ ...f, name: r.name, points: String(r.points), emoji: r.emoji }))} style={{ padding: "6px 12px", borderRadius: 20, border: `1px solid ${COLORS.border}`, background: COLORS.surfaceGreen, fontFamily: "Nunito, sans-serif", fontSize: 12, fontWeight: 600, color: COLORS.primary, cursor: "pointer" }}>
                {r.emoji} {r.name}
              </button>
            ))}
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, fontWeight: 700, color: COLORS.textMuted, marginBottom: 6 }}>Child</div>
          <select value={rewardForm.childId} onChange={e => setRewardForm(f => ({ ...f, childId: e.target.value }))} style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `2px solid ${COLORS.border}`, fontFamily: "Nunito, sans-serif", fontSize: 14, background: COLORS.surfaceGreen, color: COLORS.text, outline: "none" }}>
            <option value="">Select a child...</option>
            {children.map(c => <option key={c.id} value={c.id}>{c.avatar} {c.name}</option>)}
          </select>
        </div>
        <Input label="Reward Name" value={rewardForm.name} onChange={v => setRewardForm(f => ({ ...f, name: v }))} placeholder="Movie night" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Input label="Coins Required" value={rewardForm.points} onChange={v => setRewardForm(f => ({ ...f, points: v }))} placeholder="50" type="number" />
          <Input label="Emoji" value={rewardForm.emoji} onChange={v => setRewardForm(f => ({ ...f, emoji: v }))} placeholder="🏆" />
        </div>
        <Button onClick={addReward} style={{ width: "100%" }} size="lg" disabled={saving}>
          {saving ? "Saving..." : "Add Reward 🏆"}
        </Button>
      </Modal>

      <Modal show={showCode} onClose={() => setShowCode(false)} title="🔑 Family Code">
        <div style={{ textAlign: "center" }}>
          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 14, color: COLORS.textMuted, marginBottom: 16 }}>Share this code with your kids so they can log in</div>
          <div style={{ fontFamily: "Fredoka One, sans-serif", fontSize: 42, color: COLORS.primary, letterSpacing: 6, padding: "24px", background: COLORS.surfaceGreen, borderRadius: 16, border: `2px dashed ${COLORS.border}`, marginBottom: 16 }}>{parent.family_code}</div>
          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: COLORS.textMuted }}>Kids enter this code, pick their name, then enter their PIN</div>
        </div>
      </Modal>
      <Modal show={!!editTask} onClose={() => setEditTask(null)} title="Edit Mission ✏️">
        <Input label="Mission Name" value={editTaskForm.name || ""} onChange={v => setEditTaskForm(f => ({ ...f, name: v }))} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Input label="Coins" value={editTaskForm.points || ""} onChange={v => setEditTaskForm(f => ({ ...f, points: v }))} type="number" />
          <Input label="Emoji" value={editTaskForm.emoji || ""} onChange={v => setEditTaskForm(f => ({ ...f, emoji: v }))} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, fontWeight: 700, color: COLORS.textMuted, marginBottom: 6 }}>Frequency</div>
          <select value={editTaskForm.frequency || "Daily"} onChange={e => setEditTaskForm(f => ({ ...f, frequency: e.target.value }))} style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `2px solid ${COLORS.border}`, fontFamily: "Nunito, sans-serif", fontSize: 14, background: COLORS.surfaceGreen, color: COLORS.text, outline: "none" }}>
            {["Daily","Weekly","One-time"].map(f => <option key={f}>{f}</option>)}
          </select>
        </div>
        <Button onClick={saveEditTask} style={{ width: "100%" }} size="lg" disabled={saving}>
          {saving ? "Saving..." : "Save Changes ✓"}
        </Button>
      </Modal>

      <Modal show={!!editReward} onClose={() => setEditReward(null)} title="Edit Reward ✏️">
        <Input label="Reward Name" value={editRewardForm.name || ""} onChange={v => setEditRewardForm(f => ({ ...f, name: v }))} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Input label="Coins Required" value={editRewardForm.points || ""} onChange={v => setEditRewardForm(f => ({ ...f, points: v }))} type="number" />
          <Input label="Emoji" value={editRewardForm.emoji || ""} onChange={v => setEditRewardForm(f => ({ ...f, emoji: v }))} />
        </div>
        <Button onClick={saveEditReward} style={{ width: "100%" }} size="lg" disabled={saving}>
          {saving ? "Saving..." : "Save Changes ✓"}
        </Button>
      </Modal>

      <Modal show={!!editChild} onClose={() => { setEditChild(null); setConfirmRemoveChild(false); }} title="Edit Child ✏️">
        <Input label="Child's Name" value={editChildForm.name || ""} onChange={v => setEditChildForm(f => ({ ...f, name: v }))} placeholder="Emma" />
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, fontWeight: 700, color: COLORS.textMuted, marginBottom: 6 }}>Age</div>
          <select value={editChildForm.age || ""} onChange={e => setEditChildForm(f => ({ ...f, age: e.target.value }))} style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `2px solid ${COLORS.border}`, fontFamily: "Nunito, sans-serif", fontSize: 14, color: COLORS.text, background: COLORS.surfaceGreen, outline: "none" }}>
            <option value="">Select age…</option>
            {Array.from({ length: 15 }, (_, i) => i + 4).map(age => (
              <option key={age} value={age}>{age}</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, fontWeight: 700, color: COLORS.textMuted, marginBottom: 8 }}>Choose Avatar</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {AVATARS.map(av => (
              <button key={av} onClick={() => setEditChildForm(f => ({ ...f, avatar: av }))} style={{ fontSize: 32, padding: 8, borderRadius: 12, border: `2px solid ${editChildForm.avatar === av ? COLORS.primary : COLORS.border}`, background: editChildForm.avatar === av ? COLORS.accentLighter : COLORS.surfaceGreen, cursor: "pointer", transition: "all 0.2s" }}>{av}</button>
            ))}
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, fontWeight: 700, color: COLORS.textMuted, marginBottom: 4 }}>Change Child's PIN</div>
          <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, color: COLORS.textLight, marginBottom: 8 }}>Leave blank to keep current PIN. Default is 0000.</div>
          <input
            value={editChildForm.pin || ""}
            onChange={e => { if (/^\d{0,4}$/.test(e.target.value)) setEditChildForm(f => ({ ...f, pin: e.target.value })); }}
            placeholder="New 4-digit PIN"
            type="password"
            maxLength={4}
            style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `2px solid ${editChildForm.pin && editChildForm.pin.length > 0 && editChildForm.pin.length < 4 ? COLORS.warning : COLORS.border}`, fontFamily: "Nunito, sans-serif", color: COLORS.text, background: COLORS.surfaceGreen, outline: "none", boxSizing: "border-box", letterSpacing: 8, textAlign: "center", fontSize: 24 }}
          />
          {editChildForm.pin && editChildForm.pin.length > 0 && editChildForm.pin.length < 4 && (
            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: COLORS.warning, marginTop: 4 }}>PIN must be exactly 4 digits</div>
          )}
        </div>
        <Button onClick={saveEditChild} style={{ width: "100%" }} size="lg" disabled={saving || !editChildForm.name || (editChildForm.pin && editChildForm.pin.length > 0 && editChildForm.pin.length !== 4)}>
          {saving ? "Saving..." : "Save Changes ✓"}
        </Button>

        <div style={{ margin: "20px 0 4px", borderTop: `1px solid ${COLORS.border}` }} />

        {!confirmRemoveChild ? (
          <button
            onClick={() => setConfirmRemoveChild(true)}
            style={{ width: "100%", padding: "12px", borderRadius: 12, border: "2px solid #FEE2E2", background: "#FFF5F5", fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, color: "#DC2626", cursor: "pointer" }}
          >
            🗑️ Remove Child
          </button>
        ) : (
          <div style={{ background: "#FFF5F5", border: "2px solid #FCA5A5", borderRadius: 14, padding: "16px 18px" }}>
            <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 14, color: "#DC2626", marginBottom: 8 }}>
              ⚠️ Remove {editChild?.name}?
            </div>
            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: "#7F1D1D", lineHeight: 1.55, marginBottom: 16 }}>
              This will permanently delete all their missions, rewards, journals, and data. <strong>This cannot be undone.</strong>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={removeChild}
                disabled={saving}
                style={{ flex: 1, padding: "12px", borderRadius: 10, border: "none", background: "#DC2626", color: "#fff", fontFamily: "Nunito, sans-serif", fontWeight: 800, fontSize: 14, cursor: saving ? "default" : "pointer", opacity: saving ? 0.7 : 1 }}
              >
                {saving ? "Removing…" : "Yes, Remove"}
              </button>
              <button
                onClick={() => setConfirmRemoveChild(false)}
                style={{ flex: 1, padding: "12px", borderRadius: 10, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textMuted, fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer" }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal show={showJournal} onClose={() => setShowJournal(false)} title={`${journalChild?.avatar} ${journalChild?.name}'s Journal 📓`}>
        {journalEntries.length === 0 ? (
          <div style={{ textAlign: "center", padding: 32 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
            <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 14, color: COLORS.textMuted }}>No journal entries yet</div>
          </div>
        ) : journalEntries.map(entry => (
          <div key={entry.id} style={{ borderBottom: `1px solid ${COLORS.border}`, paddingBottom: 16, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              {entry.mood && <div style={{ fontSize: 28 }}>{entry.mood}</div>}
              <div>
                <div style={{ fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 14, color: COLORS.text }}>{entry.mood ? ["😄","😊"].includes(entry.mood) ? "Feeling great!" : entry.mood === "😐" ? "Feeling okay" : entry.mood === "😔" ? "Feeling sad" : "Feeling frustrated" : "Journal entry"}</div>
                <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 11, color: COLORS.textMuted }}>{new Date(entry.created_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
              </div>
              <button onClick={async () => {
                const newHearted = !journalHearts[entry.id];
                setJournalHearts(h => ({ ...h, [entry.id]: newHearted }));
                console.log('[ThriveTrack] heart update — entry.id:', entry.id, 'newHearted:', newHearted);
                const { error: heartErr } = await supabase.from('journals').update({ parent_hearted: newHearted }).eq('id', entry.id);
                if (heartErr) { console.error('[ThriveTrack] heart save failed:', heartErr); return; }
                setJournalEntries(es => es.map(e => e.id === entry.id ? { ...e, parent_hearted: newHearted } : e));
              }} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", fontSize: 22 }}>
                {journalHearts[entry.id] ? "❤️" : "🤍"}
              </button>
            </div>
            {entry.entry && <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 13, color: COLORS.text, background: COLORS.surfaceGreen, borderRadius: 10, padding: "8px 12px", marginBottom: 8 }}>{entry.entry}</div>}
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                value={journalComments[entry.id] || ""}
                onChange={e => setJournalComments(c => ({ ...c, [entry.id]: e.target.value }))}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && journalComments[entry.id]?.trim()) {
                    const commentText = journalComments[entry.id].trim();
                    console.log('[ThriveTrack] comment save (Enter) — entry.id:', entry.id, 'text:', commentText);
                    const { error: commentErr } = await supabase.from('journals').update({ parent_comment: commentText }).eq('id', entry.id);
                    if (commentErr) { console.error('[ThriveTrack] comment save failed:', commentErr); return; }
                    setJournalEntries(es => es.map(e => e.id === entry.id ? { ...e, parent_comment: commentText } : e));
                  }
                }}
                placeholder="Leave a comment and press Enter..."
                style={{ flex: 1, padding: "8px 12px", borderRadius: 10, border: `1px solid ${COLORS.border}`, fontFamily: "Nunito, sans-serif", fontSize: 13, outline: "none" }}
              />
              <button onClick={async () => {
                if (!journalComments[entry.id]?.trim()) return;
                const commentText = journalComments[entry.id].trim();
                console.log('[ThriveTrack] comment save (button) — entry.id:', entry.id, 'text:', commentText);
                const { error: commentErr } = await supabase.from('journals').update({ parent_comment: commentText }).eq('id', entry.id);
                if (commentErr) { console.error('[ThriveTrack] comment save failed:', commentErr); return; }
                setJournalEntries(es => es.map(e => e.id === entry.id ? { ...e, parent_comment: commentText } : e));
                setJournalComments(c => ({ ...c, [`${entry.id}_saved`]: true }));
                setTimeout(() => setJournalComments(c => { const n = {...c}; delete n[`${entry.id}_saved`]; return n; }), 2000);
              }} style={{ background: COLORS.primary, border: "none", borderRadius: 10, color: "#fff", padding: "8px 14px", fontFamily: "Nunito, sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                {journalComments[`${entry.id}_saved`] ? "✓" : "💬"}
              </button>
            </div>
            {entry.parent_comment && <div style={{ fontFamily: "Nunito, sans-serif", fontSize: 12, color: COLORS.primary, marginTop: 6, padding: "6px 10px", background: COLORS.accentLighter, borderRadius: 8 }}>💬 Your comment: {entry.parent_comment}</div>}
          </div>
        ))}
      </Modal>
    </div>
  );
}
